/**
 * Whiteboard State Capture Utility
 *
 * Captures the complete tldraw whiteboard state across all slides/pages
 * and formats it for export to the LessonWise backend API.
 *
 * The backend expects a WhiteboardState object with the following structure:
 * {
 *   presentationId: string,
 *   currentSlide: number,
 *   zoom: number,
 *   xOffset: number,
 *   yOffset: number,
 *   slides: WhiteboardSlide[]
 * }
 */

/**
 * Capture complete whiteboard state across all slides/pages
 *
 * @param {Object} editor - The tldraw editor instance (tlEditorRef.current)
 * @param {string} presentationId - The current presentation ID
 * @param {number} currentSlideNumber - The current slide number being viewed
 * @returns {Object|null} WhiteboardState object or null if editor is not available
 */
export const captureWhiteboardState = (editor, presentationId, currentSlideNumber) => {
  if (!editor) {
    console.warn('[WB State Capture] Editor not available');
    return null;
  }

  try {
    const camera = editor.getCamera();
    const allRecords = editor.store.allRecords();

    // Get all pages (slides) and sort by index to ensure correct order
    const pages = allRecords
      .filter((r) => r.typeName === 'page')
      .sort((a, b) => (a.index || '').localeCompare(b.index || ''));

    // Get all shapes
    const allShapes = allRecords.filter((r) => r.typeName === 'shape');

    // Group shapes by their parent page
    const shapesByPage = {};
    allShapes.forEach((shape) => {
      const pageId = shape.parentId;
      if (!shapesByPage[pageId]) {
        shapesByPage[pageId] = [];
      }
      shapesByPage[pageId].push(shape);
    });

    // Build slides array with annotations for each page
    const slides = pages.map((page, index) => {
      const pageShapes = shapesByPage[page.id] || [];

      // Filter out system shapes (boundary, background, poll)
      const annotations = pageShapes
        .filter(
          (s) =>
            !s.id.startsWith('shape:BOUNDARY') &&
            !s.id.startsWith('shape:BG') &&
            s.type !== 'poll'
        )
        .map((shape) => ({
          annotationId: shape.id,
          userId: shape.meta?.createdBy || '',
          type: shape.type,
          data: {
            ...shape.props,
            x: shape.x,
            y: shape.y,
            rotation: shape.rotation,
          },
          createdAt: shape.meta?.createdAt || new Date().toISOString(),
        }));

      return {
        slideId: page.id,
        slideNumber: index + 1,
        annotations,
      };
    });

    const state = {
      presentationId: presentationId || '',
      currentSlide: currentSlideNumber || 0,
      zoom: camera?.z || 1,
      xOffset: camera?.x || 0,
      yOffset: camera?.y || 0,
      slides,
    };

    console.log(
      `[WB State Capture] Captured state: ${slides.length} slides, ${allShapes.length} total shapes`
    );

    return state;
  } catch (error) {
    console.error('[WB State Capture] Error capturing state:', error);
    return null;
  }
};

/**
 * Get a summary of the current whiteboard state for logging
 *
 * @param {Object} state - WhiteboardState object
 * @returns {string} Human-readable summary
 */
export const getStateSummary = (state) => {
  if (!state) return 'No state';

  const totalAnnotations = state.slides.reduce(
    (sum, slide) => sum + (slide.annotations?.length || 0),
    0
  );

  return `Slides: ${state.slides.length}, Annotations: ${totalAnnotations}, Current: ${state.currentSlide}`;
};
